import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseCreateDailyMachineMutation, UseDailyMachineMutation } from '../../../../hooks/mutations/useDailyMachineMutation';
import { useMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { DailyMachine } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateInfos, materialState } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';
import QuantityToggleGroup from '../../../QuantityToggleGroup';

interface ManageValidationDailyMachineModalProps extends ModalProps {
    refetch: () => void;
    dailyMachine: DailyMachine | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyMachineModal: React.FC<ManageValidationDailyMachineModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyMachine,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const [currentDailyMachineId] = useState(dailyMachine?._id || '');
    const [selectedMachine, setSelectedMachine] = useState({
        label: dailyMachine?.machineId.designation || '',
        value: dailyMachine?.machineId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyMachine?.activityId)?.label || '',
        value: dailyMachine?.activityId || '',
    });
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({
        label: capitalizeFirstLetter(t(getMaterialStateInfos(dailyMachine?.state ?? materialState.PRESENT))),
        value: dailyMachine?.state.toString() || materialState.PRESENT.toString(),
    });
    const [quantity, setQuantity] = useState<number>(dailyMachine?.quantity ?? 1);

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.NEW))), value: materialState.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.PRESENT))), value: materialState.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.REMOVE))), value: materialState.REMOVE.toString() },
    ];

    const { data: dataMachines } = useMachinesQuery({ queries: { active: true } });

    const { mutate: createDailyMachine } = UseCreateDailyMachineMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyMachine } = UseDailyMachineMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyMachine = async () => {
        createDailyMachine({
            activityId: selectedActivity.value,
            machineId: selectedMachine.value,
            state: parseInt(selectedState.value),
            quantity,
            isUsed: true,
        });
    };

    const patchDailyMachine = async () => {
        if (currentDailyMachineId)
            updateDailyMachine({
                id: currentDailyMachineId,
                body: {
                    activityId: selectedActivity.value,
                    machineId: selectedMachine.value,
                    state: parseInt(selectedState.value),
                    quantity,
                },
            });
    };

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('machine')}
            subTitle={!!currentDailyMachineId.length ? t('update') : t('add')}
            description={siteName}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentDailyMachineId.length ? postDailyMachine() : patchDailyMachine();
            }}
            disabledOk={!selectedMachine.value.length || !selectedActivity.value.length || !selectedState.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedMachine}
                items={
                    dataMachines?.map((machine) => ({
                        label: machine.designation,
                        value: machine._id,
                        group: machine.machineTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedMachine({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('machine')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            <QuantityToggleGroup value={quantity} onChange={(value) => setQuantity(value)} />
        </ModalBody>
    );
};

export default ManageValidationDailyMachineModal;
